import React, { useCallback, useState } from 'react';

// Lazy-loaded so the heavy PDF viewer libs (@react-pdf-viewer/* + pdfjs-dist)
// stay out of the main bundle and only download when a preview is opened.
const PdfViewerDialog = React.lazy(() => import('../components/PdfViewerDialog'));

interface PreviewRequest {
  /** Async loader resolving the PDF as a Blob (react-pdf .toBlob(), jsPDF
   *  doc.output('blob'), or an authenticated API download). */
  getBlob: () => Promise<Blob>;
  /** File name shown in the viewer header. */
  fileName: string;
}

/**
 * Shared "preview before print/download" plumbing. Drop into any list/page that
 * already generates a PDF:
 *
 *   const { openPreview, pdfPreviewElement } = usePdfPreview();
 *   ...
 *   openPreview({ getBlob: () => getFacturePDFBlob(facture), fileName: `Facture_${facture.reference}.pdf` });
 *   ...
 *   return (<>{...page...}{pdfPreviewElement}</>);
 *
 * The viewer's toolbar provides Print + Download, so callers no longer trigger
 * the download themselves.
 */
export function usePdfPreview() {
  const [preview, setPreview] = useState<PreviewRequest | null>(null);

  const openPreview = useCallback((req: PreviewRequest) => setPreview(req), []);
  const closePreview = useCallback(() => setPreview(null), []);

  const pdfPreviewElement = preview ? (
    <React.Suspense fallback={null}>
      <PdfViewerDialog
        open
        getBlob={preview.getBlob}
        fileName={preview.fileName}
        onClose={closePreview}
      />
    </React.Suspense>
  ) : null;

  return { openPreview, closePreview, pdfPreviewElement };
}

export default usePdfPreview;
